import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import '@/styles/milk-landing.css';

const MOCKUP_HOME = '/assets/mockups/mockup-home.png';
const MOCKUP_MOBILE = '/assets/mockups/mockup-mobile.png';
const MOCKUP_CARD = '/assets/mockups/mockup-card.png';
const PHOTO_POUR = '/assets/photography/pour.jpg';

// ── inline icon helper ──────────────────────────────────────────────
const Ico = ({ d, children, fill = 'none', sw = 2 }) => (
  <svg viewBox="0 0 24 24" fill={fill} stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
    {d ? <path d={d} /> : children}
  </svg>
);

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const rootRef = useRef(null);
  const [subscribed, setSubscribed] = useState(false);
  const [showSticky, setShowSticky] = useState(false);

  const goSignup = () => navigate(user ? '/' : '/register');
  const goLogin = () => navigate(user ? '/' : '/login');

  // reveal on scroll
  useEffect(() => {
    const els = rootRef.current?.querySelectorAll('.reveal') || [];
    if (!('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(x => { if (x.isIntersecting) { x.target.classList.add('in'); io.unobserve(x.target); } });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  // mobile sticky CTA — show once the hero CTA scrolls out of view
  useEffect(() => {
    const heroCta = rootRef.current?.querySelector('.hero-cta');
    if (!heroCta || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(([e]) => setShowSticky(!e.isIntersecting), { threshold: 0 });
    io.observe(heroCta);
    return () => io.disconnect();
  }, []);

  return (
    <div className="mlk-land" ref={rootRef} data-testid="landing-page">
      {/* NAV */}
      <MarketingHeader linkPrefix="#" />

      <main id="top">
        {/* HERO */}
        <section className="hero">
          <div className="container hero-grid">
            <div className="hero-copy">
              <div className="hero-token reveal">
                <span>The execution layer for conversation</span>
              </div>
              <h1 className="reveal">You talked.<br /><em>It's handled.</em></h1>
              <p className="sub reveal">Milk captures your calls, meetings, and site visits, then keeps you on top of every task, follow-up email, and next step — so the work keeps moving and nothing slips. In under sixty seconds.</p>
              <div className="hero-cta reveal">
                <button className="btn btn-primary btn-lg" onClick={goSignup} data-testid="landing-hero-cta">
                  Get started free
                  <Ico sw={2.2}><line x1="4" y1="12" x2="18" y2="12" /><path d="m12 6 6 6-6 6" /></Ico>
                </button>
                <a className="btn btn-ghost btn-lg" href="#how" data-testid="landing-hero-secondary">See it work
                  <Ico sw={2.2}><path d="m9 6 6 6-6 6" /></Ico>
                </a>
              </div>
              <div className="hero-mini reveal">
                <Ico sw={2}><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></Ico>
                No credit card required · Set up in two minutes
              </div>
              <div className="trustline reveal">
                <span><span className="dot" />Captures calls &amp; meetings</span>
                <span><span className="dot" />Web, desktop &amp; mobile</span>
                <span><span className="dot" />Action Cards in &lt;60s</span>
              </div>
            </div>
            <div className="hero-art reveal">
              <img className="browser" src={MOCKUP_HOME} alt="The Milk home screen" />
              <img className="phone" src={MOCKUP_MOBILE} alt="Milk on mobile showing an Action Card" />
              <img className="hero-phone-m" src={MOCKUP_MOBILE} alt="Milk on mobile showing an Action Card" />
            </div>
          </div>
          <div className="proofbar">
            <div className="container proofbar-in">
              <div className="pf"><Ico><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Ico>Capture to card in under a minute</div>
              <div className="pf"><Ico><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="m3.5 7 8.5 6 8.5-6" /></Ico>Captures calls, meetings &amp; site visits</div>
              <div className="pf"><Ico><path d="M20 6 9 17l-5-5" /></Ico>Every next step tracked — nothing slips</div>
            </div>
          </div>
        </section>

        {/* STORY */}
        <section className="story" id="story">
          <div className="container story-grid">
            <div className="story-copy reveal">
              <div className="eyebrow">The difference</div>
              <div className="big">Everyone else stops at notes.<br /><em>Milk keeps you driving it forward.</em></div>
              <p>If communication is where work starts, Milk is what keeps it moving. Note-takers and memory tools make you a better record-keeper — they hand you a transcript and a tidy summary, then leave the actual follow-through to you.</p>
              <p>Milk keeps you on top of it. It turns the conversation into tracked tasks with owners and deadlines, drafts the follow-up emails, and lines up every next step — then keeps the whole thing driving forward so nothing slips. You stay in control; Milk makes sure it keeps moving.</p>
              <p className="serif" style={{ fontSize: '22px', color: 'var(--blue-d)', fontStyle: 'italic', marginTop: '6px' }}>Conversations in. Follow-through out — before your coffee's cold.</p>
            </div>
            <div className="story-photo reveal">
              <img src={PHOTO_POUR} alt="A carton of Milk pouring out Action Cards" />
              <span className="cap">Every conversation, driven forward.</span>
            </div>
          </div>
        </section>

        {/* SERVICES / HOW */}
        <section className="services" id="how">
          <div className="container">
            <div className="head reveal">
              <div className="eyebrow">How Milk works</div>
              <h2>Four steps. <em>Zero busywork.</em></h2>
              <p>From the moment a conversation ends, Milk does the part everyone dreads — turning what was said into what happens next.</p>
            </div>
            <div className="svc-grid">
              <div className="svc-steps">
                {[
                  { n: '01', t: 'Capture every conversation', p: 'Calls, client meetings, site walk-throughs — Milk captures them all. The moment something\u2019s decided, it\u2019s already on the record.', ic: <><path d="M12 2a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V5a3 3 0 0 1 3-3z" /><path d="M5 11a7 7 0 0 0 14 0" /><line x1="12" y1="18" x2="12" y2="22" /></> },
                  { n: '02', t: 'Get an Action Card', p: 'In under a minute, the conversation becomes a card: a clean summary, the decisions made, and tasks with real owners and deadlines.', ic: <><rect x="3" y="5" width="18" height="14" rx="2.5" /><line x1="3" y1="10" x2="21" y2="10" /></> },
                  { n: '03', t: 'Milk drives the follow-through', p: 'The recap email is drafted, the next step is teed up, the reminder is set. You review, tweak, and keep things moving — Milk makes sure they do.', ic: <><path d="m22 2-7 20-4-9-9-4 20-7z" /><path d="M22 2 11 13" /></> },
                  { n: '04', t: 'Stay on top of it', p: 'A confidence score and a shared timeline show exactly what\u2019s moving, what\u2019s drafted, and the one thing that still needs you.', ic: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" /></> },
                ].map(s => (
                  <div className="svc-step reveal" key={s.n}>
                    <div className="svc-ic"><Ico>{s.ic}</Ico></div>
                    <div><h3><span className="n">{s.n}</span>{s.t}</h3><p>{s.p}</p></div>
                  </div>
                ))}
              </div>
              <div className="svc-art reveal">
                <img src={MOCKUP_CARD} alt="A Milk Action Card with summary, decisions, tasks, and next steps" />
              </div>
            </div>
          </div>
        </section>

        {/* WHO */}
        <section className="who" id="who">
          <div className="container">
            <div className="head reveal">
              <div className="eyebrow">Built for professionals who work on the go</div>
              <h2>Your conversations happen in the field. <em>So does your follow-up.</em></h2>
              <p>If your day is back-to-back client conversations, Milk is the teammate who keeps you on top of everything that comes after — from the truck, the car, or the lobby.</p>
            </div>
            <div className="who-grid">
              <div className="who-phone reveal"><img src={MOCKUP_MOBILE} alt="Milk on a phone" /></div>
              <div className="who-list">
                {[
                  { t: 'Field Contractors', p: 'Walk the site, talk the scope — and drive away with the quote, punch list, and recap already drafted.', ic: <><path d="M3 14a9 9 0 0 1 18 0" /><line x1="2" y1="14" x2="22" y2="14" /><path d="M12 5V3" /><path d="M8.5 5.5 7 4" /><path d="m15.5 5.5 1.5-1.5" /></> },
                  { t: 'Real Estate Agents', p: 'Every showing and call turns into the follow-up, the next viewing, and the CRM update — without touching a keyboard.', ic: <><path d="M3 11.5 12 4l9 7.5" /><path d="M5 10v10h14V10" /><rect x="10" y="14" width="4" height="6" /></> },
                  { t: 'Financial Advisors', p: 'Turn client reviews into action items, plain-English recaps, and clean notes for the file — automatically.', ic: <><path d="M3 3v18h18" /><path d="m7 15 4-4 3 3 5-6" /></> },
                  { t: 'Insurance Brokers', p: 'Capture the policy conversation; Milk drafts the renewal, the quote, and the next touch before you\u2019ve left.', ic: <><path d="M12 2a9 9 0 0 1 9 9H3a9 9 0 0 1 9-9z" /><line x1="12" y1="11" x2="12" y2="20" /><path d="M12 20a3 3 0 0 0 3-3" /></> },
                  { t: 'Consultants', p: 'Leave the meeting with the recap, the deliverable list, and the next steps already moving in your stack.', ic: <><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></> },
                ].map(r => (
                  <div className="who-row reveal" key={r.t}>
                    <div className="who-ic"><Ico>{r.ic}</Ico></div>
                    <div><h3>{r.t}</h3><p>{r.p}</p></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* PROOF */}
        <section className="proof" id="proof">
          <div className="container">
            <div className="head center reveal">
              <div className="eyebrow">Loved by people on the go</div>
              <h2>Less managing. <em>More done.</em></h2>
            </div>
            <div className="t-grid">
              {[
                { q: '"I leave the site and the estimate\u2019s already drafted, waiting on my okay."', av: 'MT', nm: 'Marcus T.', rl: 'General Contractor' },
                { q: '"The first tool that keeps me on top of the admin instead of just reminding me."', av: 'DR', nm: 'Dana R.', rl: 'Real Estate Agent' },
                { q: '"My follow-ups used to pile up till Friday. Now they\u2019re teed up before I leave the lobby."', av: 'PN', nm: 'Priya N.', rl: 'Financial Advisor' },
              ].map(t => (
                <div className="t-card reveal" key={t.av}>
                  <div className="q">{t.q}</div>
                  <div className="who"><div className="av">{t.av}</div><div><div className="nm">{t.nm}</div><div className="rl">{t.rl}</div></div></div>
                </div>
              ))}
            </div>
            <div className="t-stats reveal">
              <div className="t-stat"><div className="v">&lt;60s</div><div className="l">Conversation to Action Card</div></div>
              <div className="t-stat"><div className="v">0</div><div className="l">Dropped follow-ups</div></div>
              <div className="t-stat"><div className="v">100%</div><div className="l">Of next steps tracked</div></div>
            </div>
          </div>
        </section>

        {/* NEWSLETTER */}
        <section className="news">
          <div className="container">
            <div className="news-card reveal">
              <h2>Notes from the creamery.</h2>
              <p>Product updates and the occasional field note on getting more done. No spam, no filler.</p>
              {!subscribed ? (
                <form className="news-form" onSubmit={(e) => { e.preventDefault(); setSubscribed(true); }}>
                  <input type="email" placeholder="you@work.com" aria-label="Email address" required data-testid="landing-newsletter-input" />
                  <button className="btn btn-primary" type="submit">Subscribe</button>
                </form>
              ) : null}
              <div className="news-note" style={subscribed ? { color: '#4C73A6', fontSize: '15px' } : undefined}>
                {subscribed ? "You're on the list. Welcome to the creamery." : 'Join the list — unsubscribe in one click, any time.'}
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="cta">
          <div className="container">
            <div className="cta-band reveal">
              <h2>Stop taking notes.<br />Start driving it forward.</h2>
              <p>Start free and let Milk keep you on top of every conversation. Upgrade when it's saving you hours every week — not before.</p>
              <div className="cta-actions">
                <button className="btn btn-cream btn-lg" onClick={goSignup} data-testid="landing-footer-cta">Get started free</button>
                <button className="btn btn-ghost-cream btn-lg" onClick={goLogin}>Sign in</button>
              </div>
              <div className="cta-mini">No credit card · Set up in two minutes</div>
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <MarketingFooter linkPrefix="#" />

      {/* Mobile sticky conversion CTA */}
      <div className={`sticky-cta${showSticky ? ' show' : ''}`} data-testid="sticky-mobile-cta">
        <span className="sc-label">Start capturing for <em>free</em></span>
        <button className="btn btn-primary" onClick={goSignup} data-testid="sticky-mobile-cta-button">Get started</button>
      </div>
    </div>
  );
}
